import type * as React from 'react'
import { rsvpCellToLabelKey } from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeRsvpStatus } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/**
 * Tone per invite answer: accepted is the good outcome (green), declined the bad
 * one (red), and the two "has not answered" states stay neutral. Exported
 * because the attendee-detail rail paints the same four states and a second copy
 * of this map would drift from it.
 */
export const RSVP_TONE: Record<MarqueeRsvpStatus, string> = {
	rsvp_yes: 'bg-success-600/10 text-success-700',
	declined: 'bg-red-700/10 text-red-700',
	no_response: 'bg-grey-200 text-grey-1000',
	invited: 'bg-grey-200 text-grey-1000'
}

interface RsvpStatusBadgeProps {
	/**
	 * The stored invite answer, or `null` when the row carries none: the import
	 * brought no RSVP fact for it, or brought a value it could not read.
	 */
	status: MarqueeRsvpStatus | null | undefined
	className?: string
	/**
	 * Trailing content inside the pill — the select's chevron and its saving
	 * spinner, when the badge is itself the control. Keeping them inside means
	 * the editable and read-only cells are the same shape, differing only by an
	 * affordance rather than by a wrapper.
	 */
	children?: React.ReactNode
}

/**
 * The roster's invite answer, `null` included.
 *
 * A missing answer is rendered as its own state ("No RSVP") rather than left
 * blank or folded into `invited`, which is what the funnel lane used to do: the
 * backend derives `funnel_status` as `rsvp_status ?? invited`, so a row with no
 * answer read as "Invited" while the cell beside it flagged the gap. One fact,
 * one place.
 */
export function RsvpStatusBadge({
	status,
	className,
	children
}: RsvpStatusBadgeProps) {
	const { t } = useTranslation()
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1 rounded-full px-3 py-1.5 font-medium text-xs',
				status ? RSVP_TONE[status] : 'bg-grey-200 text-grey-500',
				className
			)}
		>
			{t(rsvpCellToLabelKey(status))}
			{children}
		</span>
	)
}
